import React from "react";
import { createPortal } from "react-dom";
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { useAoiSelector, AoiSelectorItem } from "../../../hooks/useAoiSelector";
import ClavaImage from "../ClavaImage";

export type { AoiSelectorItem };

type AoiSelectorProps<T extends string | number> = {
  onChange: (val: T | undefined) => void;
  value?: T;
  placeholder?: string;
  disabled?: boolean;
  withoutChoose?: boolean;
  label?: string;
  iconL?: FontAwesomeIconProps["icon"];
  items: AoiSelectorItem<T>[];
  transparent?: boolean;
  primary?: boolean;
  className?: string;
  containerClassName?: string;
  autoOpen?: boolean;
};

function AoiSelector<T extends string | number>({
  onChange,
  value = undefined,
  placeholder = undefined,
  disabled = undefined,
  withoutChoose = undefined,
  label = undefined,
  primary = undefined,
  iconL = undefined,
  transparent = undefined,
  items,
  className,
  containerClassName,
  autoOpen,
}: AoiSelectorProps<T>) {
  const {
    containerRef,
    dropdownId,
    realPlaceholder,
    inputClass,
    realValue,
    selectedRegion,
    dropdownOverlay,
    onOpen,
    isOpen,
    rect,
  } = useAoiSelector({
    onChange,
    value,
    placeholder,
    disabled,
    withoutChoose,
    items,
    autoOpen,
    transparent,
    primary,
    className,
    iconL,
  });

  const selectedValueContent = realValue ? (
    <span className="flex items-center gap-2 min-w-0">
      {selectedRegion?.image && (
        <ClavaImage
          image={selectedRegion.image}
          width={24}
          round
          forceAspectRatio={1}
          className="w-6 h-6 min-w-6 min-h-6 bg-light-gray dark:bg-light-gray-dark"
          objectFit="contain"
        />
      )}
      <span className="truncate">{realValue}</span>
    </span>
  ) : (
    <span className="truncate">{realPlaceholder}</span>
  );

  if (transparent && containerClassName) {
    return (
      <>
        <div className={containerClassName} id={dropdownId} ref={containerRef}>
          {!!label && <span className="font-semibold text-xs mb-1">{label}</span>}
          <button
            onClick={onOpen}
            className={className || "outline-0 relative"}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
          >
            {selectedValueContent}
          </button>
          <button
            type="button"
            onClick={onOpen}
            aria-label={realValue ?? realPlaceholder}
            className="absolute right-3 inset-y-0 flex items-center pointer-events-auto"
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`w-4 h-4 ${
                primary ? "!text-primary" : "text-font dark:text-font-dark"
              }`}
            />
          </button>
          {!!iconL && (
            <span className="absolute left-3 inset-y-0 flex items-center pointer-events-none">
              <FontAwesomeIcon
                icon={iconL}
                className="w-4 h-4 text-font dark:text-font-dark"
              />
            </span>
          )}
        </div>
        {typeof document !== "undefined" &&
          createPortal(dropdownOverlay, document.body)}
      </>
    );
  }

  return (
    <>
      <div
        className={`relative flex flex-col items-start justify-stretch w-full${
          containerClassName ? ` ${containerClassName}` : ""
        }`}
        id={dropdownId}
        ref={containerRef}
      >
        {!!label && <span className="font-semibold text-xs mb-1">{label}</span>}
        <button
          onClick={onOpen}
          className={inputClass}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={!!rect}
        >
          {selectedValueContent}
        </button>
        <button
          type="button"
          onClick={onOpen}
          aria-label={realValue ?? realPlaceholder}
          className="absolute right-3 inset-y-0 flex items-center"
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`w-4 h-4 ${
              primary ? "!text-primary" : "text-font dark:text-font-dark"
            }`}
          />
        </button>
        {!!iconL && (
          <span className="absolute left-3 inset-y-0 flex items-center pointer-events-none">
            <FontAwesomeIcon
              icon={iconL}
              className="w-4 h-4 text-font dark:text-font-dark"
            />
          </span>
        )}
      </div>
      {typeof document !== "undefined" &&
        createPortal(dropdownOverlay, document.body)}
    </>
  );
}

export default AoiSelector;
